import React, { useState } from 'react';
import { AddIcon } from 'cokitchen-icons';
import {
  Button,
  Input,
  SideModal,
  ToastMessage,
  formatPhoneNumber
} from '@cokitchen/cokitchen-components';
import { useParams } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import CurrencyInput from 'components/base/currency-input';
import { handleApiError } from 'utils/functions';
import { GuestDetailsType } from 'models/guest.model';

type Props = {
  eventId?: string;
  isOpen: boolean;
  onClose: () => void;
};
const defaultData = () => {
  return {
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
    budget: ''
  };
};

type AddNewGuestProps = {
  guestData: GuestDetailsType;
  setGuestData: (guestData: GuestDetailsType) => void;
  handleDelete: () => void;
  showRemoveButton: boolean;
};

const AddGuestForm = (props: AddNewGuestProps) => {
  const { guestData, setGuestData, handleDelete, showRemoveButton } = props;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    const { name, value } = target;
    setGuestData({
      ...guestData,
      [name]: value
    });
  };
  return (
    <div className='max-w-[396px] text-sm space-y-4 border-b border-[#E9EAEC]'>
      {showRemoveButton && (
        <div
          onClick={handleDelete}
          className='text-[#E60A2B] text-sm my-2 cursor-pointer max-w-[110px]'>
          - <span>Remove guest</span>
        </div>
      )}

      <div className='flex gap-2 flex-wrap w-full justify-between'>
        <div className='w-full sm:w-[48%]'>
          <Input
            design='bordered'
            type='text'
            label='First Name'
            name='first_name'
            value={guestData.first_name}
            onChange={handleChange}
            placeholder='Enter a name'
          />
        </div>
        <div className='w-full sm:w-[48%]'>
          <Input
            design='bordered'
            type='text'
            label='Last Name'
            name='last_name'
            value={guestData.last_name}
            onChange={handleChange}
            placeholder='Enter a name'
          />
        </div>
      </div>
      <div className='flex gap-2 flex-wrap w-full justify-between'>
        <div className='w-full sm:w-[48%]'>
          <Input
            design='bordered'
            type='text'
            label='Email'
            name='email'
            value={guestData.email}
            onChange={handleChange}
            placeholder='Enter a name'
          />
        </div>
        <div className='w-full sm:w-[48%]'>
          <Input
            design='bordered'
            type='phone'
            label='Mobile Number'
            name='mobile_number'
            value={guestData.mobile_number}
            onChange={handleChange}
            placeholder='Mobile number'
          />
        </div>
      </div>
      <CurrencyInput
        label='Set a budget'
        value={guestData.budget}
        onChange={(budget) => {
          setGuestData({
            ...guestData,
            budget
          });
        }}
        placeholder='50,000'
      />
    </div>
  );
};

const AddMoreGuestsModal = ({ isOpen, onClose, eventId }: Props) => {
  const { activeCompany } = useDashboardContext();
  const companyId = activeCompany?.id;

  const { id } = useParams();
  const [eventGuest, setEventGuest] = useState<GuestDetailsType[]>([defaultData()]);
  const [loading, setLoading] = useState(false);
  const addMoreGuests = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const payload = {
        company_id: companyId,
        event_id: eventId ?? id,
        guest_data: eventGuest.map((item: GuestDetailsType) => {
          return {
            phone_number: formatPhoneNumber(item?.mobile_number ?? ''),
            last_name: item.last_name,
            first_name: item.first_name,
            email: item.email,
            budget: item.budget
          };
        })
      };
      await apiService.post('guest/csv', {
        data: payload
      });
      toast(<ToastMessage message='Guests added successfully' type='success' />);
      onClose();
      setEventGuest([defaultData()]);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  function hasEmptyField(data: Partial<GuestDetailsType>[]): boolean {
    for (const item of data) {
      for (const key in item) {
        if (
          typeof item[key as keyof GuestDetailsType] === 'string' &&
          item[key as keyof GuestDetailsType] === ''
        ) {
          return true;
        }
      }
    }
    return false;
  }
  return (
    <SideModal
      closeBtnVariant='side'
      bodyClassName='w-[98vw] sm:w-[450px]'
      showTopNavbar
      isOpen={isOpen}
      onRequestClose={onClose}
      title=''>
      <div>
        <p className='font-semibold text-center mb-6'>Add Guests to this event</p>
        <div>
          {eventGuest?.map((item, index) => (
            <AddGuestForm
              key={index}
              guestData={item}
              handleDelete={() => {
                setEventGuest(eventGuest.filter((itm, i) => i !== index));
              }}
              setGuestData={(newForm) => {
                const newGuests = [...eventGuest];
                newGuests[index] = newForm;
                setEventGuest(newGuests);
              }}
              showRemoveButton={index !== 0}
            />
          ))}
        </div>
        <div
          className='text-[#0080E5] flex items-center gap-[2px] text-sm font-medium cursor-pointer my-4'
          onClick={() => {
            setEventGuest([...eventGuest, defaultData()]);
          }}>
          <AddIcon className='w-5 h-5' />
          <span>Add guest</span>
        </div>

        <Button
          variant='block'
          color='primary'
          className='h-[52px] mt-2'
          onClick={addMoreGuests}
          loading={loading}
          disabled={hasEmptyField(eventGuest)}>
          Confirm
        </Button>
      </div>
    </SideModal>
  );
};

export default AddMoreGuestsModal;
